import {
  Align,
  Arrange,
  Button,
  Container,
  Icon,
  Illustration,
  IllustrationShare,
  Spacer,
  Text,
} from '@loomhq/lens';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import {
  INVITE_MEMBERS_LIBRARY_EMPTY_STATE,
  ONBOARDING_MOBILE_EMAIL,
} from 'constants/events';
import {
  PAGETYPE_SHARED,
  PAGETYPE_SHARED_WITH_ME,
  PAGETYPE_TEAM,
} from 'constants/folders';
import { TEAM_INVITE_MODAL } from 'constants/modalTypes';
import {
  MY_ARCHIVE_FILTER,
  TEAM_ARCHIVE_FILTER,
  VIEW_MEDIA_SCREENSHOTS,
} from 'constants/videosDashboard';
import { postDownloadLoomEmail } from 'creators/user';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';

import { useGetSelectedWorkspace } from 'hooks/workspace';

import { ArchivedSvg, SharedSvg, StarredSvg, TeamSvg } from './icons';
import {
  launchAddVideoToSharedIntercomTutorial,
  launchAddVideoToTeamIntercomTutorial,
} from '../onboarding-actions';

export default function EmptyLibraryDashboard({
  library,
  viewMedia,
  isRootOfPage,
  pageType,
  isViewer,
}) {
  let Component = null;

  if (pageType === 'archived') {
    Component = EmptyArchive;
  } else if (pageType === PAGETYPE_SHARED_WITH_ME) {
    Component = EmptyLibrarySharedWithMe;
  } else if (pageType === 'starred') {
    Component = EmptyStarred;
  } else if (!isRootOfPage) {
    Component = GenericEmptyLibrary;
  } else if (library === PAGETYPE_TEAM) {
    Component = EmptyLibraryForTeamLibrary;
  } else if (library === PAGETYPE_SHARED) {
    Component = EmptyLibraryForSharedLibrary;
  } else if (viewMedia === VIEW_MEDIA_SCREENSHOTS) {
    Component = () => null;
  } else {
    Component = VideosEmptyLibrary;
  }

  return <Component isViewer={isViewer} />;
}

const VideosEmptyLibrary = () => {
  const dispatch = useDispatch();
  const [isSent, setIsSent] = useState(false);

  useEffect(() => {
    if (!isSent) {
      return;
    }

    const id = setTimeout(() => {
      setIsSent(false);
    }, 3000);

    return () => {
      clearTimeout(id);
    };
  }, [isSent]);

  const emailMeALinkFn = () => {
    setIsSent(true);
    analytics.track(ONBOARDING_MOBILE_EMAIL, {
      source: 'manual',
    });

    dispatch(postDownloadLoomEmail());
  };

  return (
    <EmptyLibrary
      title="No videos recorded yet"
      description={
        <>
          <span>
            Every time you record a new video, you’ll find it here. Only you can
            manage videos in this library.
          </span>
          {isMobile && (
            <>
              <br />
              <span>
                To record, visit loom.com/download in your desktop browser.
              </span>
            </>
          )}
        </>
      }
      cta={
        isMobile && (
          <Button variant="record" onClick={emailMeALinkFn}>
            {isSent ? 'E-mail sent!' : 'E-mail me a link'}
          </Button>
        )
      }
    />
  );
};

const EmptyLibraryForTeamLibrary = () => {
  const dispatch = useDispatch();
  const selectedWorkspace = useGetSelectedWorkspace();

  if (selectedWorkspace.members < 5) {
    const onClick = () => {
      dispatch(openModal(TEAM_INVITE_MODAL));

      analytics.track(INVITE_MEMBERS_LIBRARY_EMPTY_STATE, {
        library_type: 'team',
      });
    };

    return (
      <EmptyLibrary
        centerpiece={<TeamSvg />}
        title="Move videos here for your whole team to access"
        description="Move videos that you share with teammates often so that they can access them easily at anytime."
        cta={<Button onClick={onClick}>Invite teammates</Button>}
      />
    );
  }

  return (
    <EmptyLibrary
      centerpiece={<TeamSvg />}
      title="Share with your team"
      description="This is your centralized hub to organize and share videos with everyone in this workspace. Record a video and move it here for your teammates to see."
      cta={
        <Button
          variant="neutral"
          onClick={launchAddVideoToTeamIntercomTutorial}
        >
          Show me how
        </Button>
      }
    />
  );
};

const EmptyLibraryForSharedLibrary = () => {
  const dispatch = useDispatch();
  const selectedWorkspace = useGetSelectedWorkspace();

  if (selectedWorkspace.members < 5) {
    const onClick = () => {
      dispatch(openModal(TEAM_INVITE_MODAL));

      analytics.track(INVITE_MEMBERS_LIBRARY_EMPTY_STATE, {
        library_type: 'shared',
      });
    };

    return (
      <EmptyLibrary
        centerpiece={<SharedSvg />}
        title="Invite teammates to edit a video"
        description="Invite teammates to edit a video and it will show up here. If someone invites you to a video, you can also find it here."
        cta={<Button onClick={onClick}>Invite teammates</Button>}
      />
    );
  }

  return (
    <EmptyLibrary
      centerpiece={<SharedSvg />}
      title="Invite teammates to edit videos with you"
      description="Videos that you’re working on with other people will show up here. Give it a shot: record a video and invite a teammate to edit it."
      cta={
        <Button
          variant="neutral"
          onClick={launchAddVideoToSharedIntercomTutorial}
        >
          Show me how
        </Button>
      }
    />
  );
};

const GenericEmptyLibrary = ({ isViewer }) => {
  return (
    <EmptyLibrary
      title="There’s nothing here yet!"
      description={
        isViewer ? null : 'Move a video into this folder to organize your work.'
      }
    />
  );
};

const EmptyArchive = () => {
  const archiveType = useSelector(
    state => state?.videos_dashboard?.selectedSubHeader
  );

  let description = '';

  if (archiveType === MY_ARCHIVE_FILTER) {
    description =
      'When you archive content that belongs to you, it will show up here and will only be visible to you.';
  } else if (archiveType === TEAM_ARCHIVE_FILTER) {
    description =
      'When you archive content that is in the team library, it will show up here and will be visible to everyone.';
  }

  return (
    <EmptyLibrary
      centerpiece={<ArchivedSvg />}
      title="Archive to keep your Workspace tidy"
      description={description}
    />
  );
};

const EmptyStarred = () => {
  return (
    <EmptyLibrary
      centerpiece={<StarredSvg />}
      title="Star to quickly find things later"
      description="Star any folder or video to easily find it again."
    />
  );
};

const EmptyLibrarySharedWithMe = () => {
  return (
    <EmptyLibrary
      illustration={<IllustrationShare />}
      title="You haven’t been added to any videos!"
      description="Videos that have been directly shared with you will show up here. No more losing track of videos. "
    />
  );
};

export const EmptyLibrary = ({
  title,
  description,
  cta = null,
  centerpiece = null,
  illustration = null,
  spotIllustration = null,
  spotIllustrationWidth = null,
  spotIllustrationHeight = null,
  iconColor = '#c4c4c4',
  spacerTop = 5,
  spacerBottom = 4,
  backgroundColor = 'backgroundSecondary',
  descriptionColor,
}) => {
  return (
    <Spacer top={spacerTop} bottom={spacerBottom}>
      <Container padding={3} radius="medium" backgroundColor={backgroundColor}>
        <Arrange minHeight="19.125rem" justifyContent="center">
          <div>
            <Arrange autoFlow="row" gap="medium">
              {centerpiece && (
                <Align alignment="center">
                  <Icon icon={centerpiece} size={10} color={iconColor} />
                </Align>
              )}
              {illustration && (
                <Align alignment="center">
                  <Illustration
                    color="grey5"
                    illustration={illustration}
                    size={7}
                  />
                </Align>
              )}
              {spotIllustration && (
                <Align alignment="center">
                  <img
                    src={spotIllustration}
                    alt=""
                    width={spotIllustrationWidth}
                    height={spotIllustrationHeight}
                  />
                </Align>
              )}
              <Arrange autoFlow="row" gap="small" maxWidth="26.25rem">
                <Text
                  variant="title"
                  htmlTag="h2"
                  fontWeight="bold"
                  alignment="center"
                >
                  {title}
                </Text>
                {description && (
                  <Text htmlTag="p" alignment="center" color={descriptionColor}>
                    {description}
                  </Text>
                )}
              </Arrange>
            </Arrange>
            {cta && (
              <Spacer top="large">
                <Align alignment="center">{cta}</Align>
              </Spacer>
            )}
          </div>
        </Arrange>
      </Container>
    </Spacer>
  );
};
